import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Lock, ArrowLeft, Download, Briefcase, Mail, FileText, RefreshCw } from "lucide-react";
import Logomark from "../components/Logomark";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "deppcm_admin_key";

export default function AdminLeads() {
  const [token, setToken] = useState(() =>
    typeof window !== "undefined" ? window.localStorage.getItem(TOKEN_KEY) || "" : ""
  );
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("presentations");

  useEffect(() => {
    if (token) verify(token, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verify = async (key, silent = false) => {
    setLoading(true);
    setError("");
    try {
      await axios.post(
        `${API}/admin/check`,
        {},
        { headers: { "X-Admin-Key": key } }
      );
      setAuthed(true);
      window.localStorage.setItem(TOKEN_KEY, key);
      await fetchLeads(key);
    } catch (err) {
      if (!silent) setError("Invalid admin key");
      setAuthed(false);
      window.localStorage.removeItem(TOKEN_KEY);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeads = async (key) => {
    try {
      const { data } = await axios.get(`${API}/admin/leads`, {
        headers: { "X-Admin-Key": key },
      });
      setData(data);
    } catch (err) {
      setError("Failed to fetch leads");
    }
  };

  const logout = () => {
    window.localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setData(null);
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] text-white flex items-center justify-center px-6">
        <div className="w-full max-w-md relative border border-white/10 bg-[#0F0F11] p-10">
          <div className="absolute top-4 left-4 w-5 h-5 border-t border-l border-[#D4AF37]" />
          <div className="absolute top-4 right-4 w-5 h-5 border-t border-r border-[#D4AF37]" />
          <div className="absolute bottom-4 left-4 w-5 h-5 border-b border-l border-[#D4AF37]" />
          <div className="absolute bottom-4 right-4 w-5 h-5 border-b border-r border-[#D4AF37]" />

          <div className="flex items-center gap-3 mb-8">
            <Logomark size={42} />
            <div className="border-l border-white/10 pl-3">
              <div className="font-display font-black text-sm tracking-tight">DEPP C&amp;M</div>
              <div className="text-[9px] tracking-[0.25em] uppercase text-[#8B939C] mt-1">
                Admin · Leads
              </div>
            </div>
          </div>

          <div className="overline mb-3">Admin Access</div>
          <h1 className="font-display font-bold text-white text-2xl tracking-tight mb-8">
            Enter your admin key to continue.
          </h1>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              verify(token);
            }}
            className="space-y-6"
          >
            <div className="flex flex-col">
              <label className="text-[10px] uppercase tracking-[0.25em] text-[#8B939C] mb-2">
                Admin key
              </label>
              <input
                type="password"
                data-testid="admin-key-input"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                required
                autoFocus
                className="bg-transparent border-0 border-b border-white/15 rounded-none text-white text-sm px-0 py-2 focus:outline-none focus:border-[#D4AF37] placeholder:text-[#3f3f46] tracking-wider font-mono"
              />
            </div>

            {error && (
              <div className="text-[11px] uppercase tracking-[0.2em] text-red-400 flex items-center gap-2">
                <Lock className="w-3 h-3" />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !token}
              data-testid="admin-login"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black py-3.5 text-[11px] font-bold uppercase tracking-[0.25em] hover:bg-[#F0C84B] transition-colors disabled:opacity-60"
            >
              {loading ? "Verifying..." : "Enter Dashboard"}
            </button>
          </form>

          <Link
            to="/"
            className="mt-8 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#8B939C] hover:text-[#D4AF37] transition-colors"
          >
            <ArrowLeft className="w-3 h-3" />
            Back to site
          </Link>
        </div>
      </div>
    );
  }

  const d = data || { contacts: [], careers: [], presentations: [], totals: {} };

  const tabs = [
    { id: "presentations", label: "Deck Downloads", icon: FileText, count: d.totals.presentations, ready: !!data },
    { id: "contacts", label: "Advisory Inquiries", icon: Mail, count: d.totals.contacts, ready: !!data },
    { id: "careers", label: "Career Applications", icon: Briefcase, count: d.totals.careers, ready: !!data },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-white">
      <header className="sticky top-0 z-40 bg-[#0A0A0B]/90 backdrop-blur border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <Logomark size={36} />
            <div className="border-l border-white/10 pl-3">
              <div className="font-display font-black text-[15px] tracking-tight">DEPP C&amp;M</div>
              <div className="text-[9px] tracking-[0.25em] uppercase text-[#8B939C] mt-1">
                Admin · Leads
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <button
              onClick={() => fetchLeads(token)}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 px-3 py-2 text-[11px] font-bold uppercase tracking-[0.2em] border border-white/15 hover:border-[#D4AF37] hover:text-[#D4AF37]"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="text-[11px] uppercase tracking-[0.2em] text-[#8B939C] hover:text-[#D4AF37]"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
        <div className="flex items-center gap-3 mb-5">
          <span className="h-px w-10 bg-[#D4AF37]" />
          <span className="overline">Lead dashboard</span>
        </div>
        <h1 className="font-display font-bold text-white text-[clamp(1.75rem,3vw,2.75rem)] tracking-tight leading-tight mb-10">
          All inbound leads from the site.
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 border border-white/5 mb-12">
          {tabs.map((tt) => (
            <button
              key={tt.id}
              onClick={() => setTab(tt.id)}
              data-testid={`admin-tab-${tt.id}`}
              className={`flex items-start gap-4 p-6 text-left transition-colors ${
                tab === tt.id ? "bg-[#121214]" : "bg-[#0F0F11] hover:bg-[#121214]"
              }`}
            >
              <div
                className={`w-10 h-10 border flex items-center justify-center ${
                  tab === tt.id ? "border-[#D4AF37]" : "border-white/15"
                }`}
              >
                <tt.icon className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.6} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#8B939C]">
                  {tt.label}
                </div>
                <div className="font-display font-black text-3xl text-[#D4AF37] tabular-nums tracking-tight mt-1">
                  {tt.ready ? (tt.count ?? 0) : "—"}
                </div>
              </div>
            </button>
          ))}
        </div>

        {tab === "presentations" && (
          <Table
            headers={["Date", "Company", "Name", "Email", "Country", "Template", "Language", ""]}
            rows={d.presentations.map((p) => ({
              cells: [
                fmt(p.created_at),
                p.company,
                p.name,
                p.email,
                p.country || "—",
                tagPill(p.template),
                p.language?.toUpperCase(),
                <a
                  key={p.id}
                  href={`${BACKEND_URL}${p.download_url}`}
                  target="_blank"
                  rel="noopener"
                  data-testid={`admin-download-${p.id}`}
                  className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] hover:text-[#F0C84B]"
                >
                  <Download className="w-3 h-3" />
                  PDF
                </a>,
              ],
              key: p.id,
            }))}
          />
        )}

        {tab === "contacts" && (
          <Table
            headers={["Date", "Company", "Name", "Email", "Country", "Project type"]}
            rows={d.contacts.map((p) => ({
              cells: [fmt(p.created_at), p.company, p.name, p.email, p.country || "—", p.project_type],
              key: p.id,
            }))}
          />
        )}

        {tab === "careers" && (
          <Table
            headers={["Date", "Position", "Name", "Email", "Experience", "Location"]}
            rows={d.careers.map((p) => ({
              cells: [
                fmt(p.created_at),
                p.position,
                p.name,
                p.email,
                p.experience_years != null ? `${p.experience_years} yrs` : "—",
                p.location || "—",
              ],
              key: p.id,
            }))}
          />
        )}
      </main>
    </div>
  );
}

function Table({ headers, rows }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="border border-white/5 bg-[#0F0F11] py-16 text-center text-sm text-[#8B939C]">
        No entries yet.
      </div>
    );
  }
  return (
    <div className="border border-white/5 bg-[#0F0F11] overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[#121214] border-b border-white/5">
            {headers.map((h, i) => (
              <th
                key={i}
                className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.25em] text-[#8B939C] font-bold whitespace-nowrap"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-b border-white/5 hover:bg-[#121214] transition-colors">
              {r.cells.map((c, i) => (
                <td key={i} className="px-4 py-3 text-[13px] text-[#D1D1D4] whitespace-nowrap">
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function tagPill(text) {
  return (
    <span className="inline-block px-2 py-1 text-[10px] uppercase tracking-[0.2em] border border-[#D4AF37]/30 text-[#D4AF37]">
      {text}
    </span>
  );
}

function fmt(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "2-digit",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
